/**
 * 💬 포털 사용 문의 챗봇 API — backend /helpbot/* (v2.275.0 · 2026-10-19 · 사용자 요청)
 *
 * 어느 화면에서든 [💬 사용 문의] → 채팅 창에서 질문 → 부장님 PC 의 지킴이(ops/helpbot-watch.mjs)가
 * 3초마다 보다가 집어 읽기 전용 Claude 로 답을 만든다(보통 1~2분). 답은 이 API 가 아니라 지킴이가 표에 직접 적는다.
 * 대화는 물은 사람과 관리자만 본다.
 */

import { api } from './api';
import { MENU_GROUPS, pathOnly } from './menu';

/**
 * ⛔서버 `lib/helpbot/store.ts` · DB CHECK(seq_0025) · 지킴이(`ops/helpbot-watch.mjs`)와 **같은 목록** — `verify:helpbot` 이 대조한다.
 */
export const HELPBOT_STATUSES = ['대기', '답변중', '완료', '실패', '취소'] as const;
export type HelpbotStatus = (typeof HELPBOT_STATUSES)[number];

/** 열린 질문 — 이 상태면 창이 몇 초마다 답을 다시 본다 */
export function isOpenStatus(s: HelpbotStatus): boolean {
  return s === '대기' || s === '답변중';
}

export interface HelpbotTurn {
  id: number;
  threadId: number;
  question: string;
  pagePath: string;
  pageLabel: string;
  status: HelpbotStatus;
  answer: string | null;
  askedAt: string;
  claimedAt: string | null;
  answeredAt: string | null;
  seenAt: string | null;
  elapsedMs: number | null;
  /** 어디서 물었나 — web(이 창) · works(웍스 「포털챗봇」 1:1 · v2.282.0) */
  channel: 'web' | 'works';
  /** 웍스로 답 보내기 — null=아직(또는 웹 질문) · 보내는중 · 보냄 · 못보냄 · 모름 · 안보냄 */
  worksSend: string | null;
  /** 📮 도우미가 답 끝에 적은 개발요청 제안(v2.288.0) — 있으면 [개발요청으로 올리기] 단추 */
  devreq: { title: string; body: string; menuKey: string } | null;
  /** 제안으로 올린 개발요청 번호 — 있으면 「올렸어요 · 열기」 */
  devreqId: number | null;
}

export interface HelpbotThreadRow {
  id: number;
  title: string;
  createdAt: string;
  updatedAt: string;
  turnCount: number;
  lastStatus: HelpbotStatus | null;
  unread: number;
}

export interface HelpbotThread {
  id: number;
  userId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  turns: HelpbotTurn[];
  mine: boolean;
}

export interface HelpbotState {
  online: boolean;
  lastSeenAt: string | null;
  unread: number;
  open: { turnId: number; threadId: number; status: HelpbotStatus; askedAt: string } | null;
  todayCount: number;
  dailyLimit: number;
  questionMax: number;
  threadTurnMax: number;
}

export interface HelpbotAdminTurn extends HelpbotTurn {
  userId: string;
  userName: string | null;
  userRole: string;
  /** 도우미가 「물어본 사람 자격」으로 읽은 포털 자료 주소와 결과 (v2.276.0) */
  reads: { path: string; status: number }[];
  /** 웍스로 답을 못 보낸 까닭(못보냄·모름일 때) */
  worksSendError: string | null;
}

export function getState(): Promise<HelpbotState> {
  return api.get<HelpbotState>('/helpbot/state');
}

export async function listThreads(): Promise<HelpbotThreadRow[]> {
  const r = await api.get<{ threads: HelpbotThreadRow[] }>('/helpbot/threads');
  return r.threads;
}

export function getThread(id: number): Promise<HelpbotThread> {
  return api.get<HelpbotThread>(`/helpbot/threads/${id}`);
}

export function markSeen(id: number): Promise<{ marked: number }> {
  return api.post<{ marked: number }>(`/helpbot/threads/${id}/seen`);
}

export function ask(p: { threadId: number | null; question: string; pagePath: string; pageLabel: string }): Promise<{ threadId: number; turnId: number }> {
  return api.post<{ threadId: number; turnId: number }>('/helpbot/ask', p);
}

export function cancelTurn(id: number): Promise<{ cancelled: boolean }> {
  return api.post<{ cancelled: boolean }>(`/helpbot/turns/${id}/cancel`);
}

/** 📮 도우미 제안을 내 이름으로 개발요청 글로 (v2.288.0) — 이미 올렸으면 같은 번호(created=false) */
export function fileDevRequest(turnId: number): Promise<{ id: number; created: boolean; title: string }> {
  return api.post<{ id: number; created: boolean; title: string }>(`/helpbot/turns/${turnId}/devreq`);
}

export async function listRecentTurns(): Promise<HelpbotAdminTurn[]> {
  const r = await api.get<{ turns: HelpbotAdminTurn[] }>('/helpbot/admin/turns');
  return r.turns;
}

export interface WorksLinkResult {
  linked: boolean;
  name: string;
  /** 연결 전에 웍스로 보낸 질문을 바로 넣었나 */
  queued: boolean;
  /** 맡아 둔 질문을 못 넣은 까닭 */
  notQueuedReason: 'open-exists' | 'daily-limit' | null;
  /** 웍스로 「연결됐어요」 안내가 나갔나 */
  notified: boolean;
}

/** 웍스 「포털챗봇」 연결 — 봇이 1:1 로 보낸 링크(#t=…)의 열쇠 (v2.282.0) */
export function confirmWorksLink(token: string): Promise<WorksLinkResult> {
  return api.post<WorksLinkResult>('/helpbot/works-link', { token });
}

/** 연결 링크의 # 뒤에서 열쇠를 꺼낸다 — 「#t=열쇠」 · 모양이 틀리면 null */
export function linkTokenFromHash(hash: string): string | null {
  const t = new URLSearchParams(hash.replace(/^#/, '')).get('t');
  return t && /^[A-Za-z0-9_-]{20,100}$/.test(t) ? t : null;
}

/** 웍스로 답 보내기 상태 — 관리자 「모든 문의」 표시 */
export const WORKS_SEND_LABEL: Record<string, { label: string; cls: string }> = {
  보내는중: { label: '웍스로 보내는 중', cls: 'text-slate-500' },
  보냄: { label: '웍스로 보냄', cls: 'text-emerald-700' },
  못보냄: { label: '웍스로 못 보냄', cls: 'text-red-600' },
  모름: { label: '웍스 전달 모름', cls: 'text-amber-700' },
  안보냄: { label: '웍스로 안 보냄(1시간 지남)', cls: 'text-slate-500' },
};

/**
 * 질문과 함께 보내는 「보던 화면」 — 주소의 경로만(물음표 뒤 검색어·번호는 보내지 않는다) + 메뉴 이름(「📦 재고 › 주요재고 확인」).
 *   메뉴 원본은 `lib/menu.ts` 한 곳 — 메뉴 주소와 같거나 그 아래면 그 메뉴 이름 · 아니면 이름 없이 경로만.
 */
export function pageContext(pathname: string): { pagePath: string; pageLabel: string } {
  const p = pathOnly(pathname).replace(/\/+$/, '') || '/';
  const pagePath = /^\/[A-Za-z0-9/_.-]*$/.test(p) ? p.slice(0, 200) : '';
  let best: { len: number; label: string } | null = null;
  for (const g of MENU_GROUPS) {
    for (const it of g.items) {
      const k = pathOnly(it.path);
      const hit = k === p || (k !== '/' && p.startsWith(`${k}/`));
      if (hit && (!best || k.length > best.len)) best = { len: k.length, label: `${g.heading} › ${it.label}` };
    }
  }
  if (!best && p === '/mypage') best = { len: 7, label: '👤 마이페이지' };
  return { pagePath, pageLabel: best?.label.slice(0, 80) ?? '' };
}

/**
 * 열린 질문을 다시 보는 간격 — 창이 열려 있을 때: 처음 3분은 3초 · 30분까지 30초 · 그 뒤엔 멈춘다.
 *   ⚠PC 가 꺼져 질문이 「대기」에 머문 채 창을 열어 두어도 요청이 끝없이 쌓이지 않게(배포 전 「사람이 쓰나」 검사도 흐려진다).
 */
export function waitPollMs(askedAt: string, now = Date.now()): number | false {
  const age = now - new Date(askedAt).getTime();
  if (!Number.isFinite(age)) return false;
  if (age < 3 * 60_000) return 3000;
  if (age < 30 * 60_000) return 30_000;
  return false;
}

/** 창을 닫은 채 기다릴 때 [💬] 단추가 상태를 다시 보는 간격 — 10분까지 15초(답이 오면 빨간 점) · 그 뒤엔 창을 열 때만 */
export function fabPollMs(askedAt: string, now = Date.now()): number | false {
  const age = now - new Date(askedAt).getTime();
  if (!Number.isFinite(age)) return false;
  return age < 10 * 60_000 ? 15_000 : false;
}

/** 걸린 시간 「1:05」 */
export function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
